import _ from 'underscore';
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Folder20Regular } from '@fluentui/react-icons';
import {
  Body1,
  Caption1,
  Card,
  CardFooter,
  CardPreview,
} from '@fluentui/react-components';
import { ListItem } from '@fluentui/react-list-preview';
import { getTimeDescription, FolderAssetType, VideoStatus } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';
import { useFolderCardStyles } from '../../../home-page-styles/component-styles/home-page-folder-asset-cards';

export const FolderAssetCard = ({
  folder, i18n, navigateIntoFolderItem, navigation, ariaSetsize, ariaPosinset, onClickCard,
}) => {
  const styles = useFolderCardStyles();

  // Grab up to the first 4 assets for preview on the card
  const assetsToPreview = useMemo(() => {
    const filteredAssets = _.filter(folder && folder.assets, asset =>
      (asset.folderAssetType === FolderAssetType.Image ||
        (asset.folderAssetType === FolderAssetType.Video && asset.status === VideoStatus.Active)));
    if (Array.isArray(filteredAssets) && filteredAssets.length > 0) {
      return filteredAssets.slice(0, 4);
    }
    return [];
  }, [folder]);

  const handleFolderAssetClick = useCallback((folderAsset) => {
    navigation.gotoLibraryPage();
    navigateIntoFolderItem({ folder: folderAsset });
    onClickCard();
  }, [navigateIntoFolderItem, navigation, onClickCard]);

  const handleFolderAssetKeyDown = useCallback((event, folderAsset) => {
    if (event.key === 'Enter') {
      handleFolderAssetClick(folderAsset);
      event.preventDefault();
    }
  }, [handleFolderAssetClick]);

  // Set grid layout class based on the number of assets
  const getGridClass = useMemo(() => {
    if (assetsToPreview.length === 1) return styles.oneImage; // No grid for 1 image
    if (assetsToPreview.length === 2) return styles.twoImages; // 2 images side by side
    if (assetsToPreview.length === 3) return styles.threeImages; // Grid layout for 3 images
    if (assetsToPreview.length === 4) return styles.fourImages; // Grid layout for 4 images
    return styles.imageGrid; // Default grid for other cases
  }, [styles, assetsToPreview]);

  const modifiedTime = useMemo(() => {
    if (folder.modifiedTime) {
      return getTimeDescription(folder.modifiedTime, i18n);
    }
    return '';
  }, [folder.modifiedTime, i18n]);

  const modifiedTimeString = useMemo(() => (modifiedTime ? `${i18n.getString(_TL_('Accessed:'))} ${modifiedTime}` : ''), [i18n, modifiedTime]);

  return (
    <ListItem
      aria-posinset={ariaPosinset}
      aria-setsize={ariaSetsize}
      aria-label={`Preview of folder ${folder.folderName} ${modifiedTimeString}`}
      onClick={() => handleFolderAssetClick(folder)}
      onKeyDown={event => handleFolderAssetKeyDown(event, folder)}
      className={styles.folderCardListItem}
    >
      <Card
        className={styles.card}
      >
        <CardPreview className={styles.cardPreview}>
          <div className={styles.imageGrid}>
            <div className={`${getGridClass}`}>
              {assetsToPreview && assetsToPreview.length > 0 && assetsToPreview.map(asset => (
                <img
                  key={asset.id}
                  // check asset for thumbnailUrl first for video asset case, url points to video file
                  src={asset.thumbnailUrl || asset.url || asset.originalUrl}
                  alt={`Preview of folder ${folder.folderName}`}
                  className={styles.thumbnailImage}
                />
              ))}
            </div>
          </div>
        </CardPreview>
        <div className={styles.footer}>
          <CardFooter className={styles.footerContent}>
            <div className={styles.iconAndName}>
              <Folder20Regular style={{ marginRight: '8px', fontSize: 20 }} />
              <Body1 className={styles.folderName} title={folder.folderName}>{folder.folderName}</Body1>
            </div>
            <div className={styles.caption}>
              <Caption1>
                {modifiedTimeString}
              </Caption1>
            </div>
          </CardFooter>
        </div>
      </Card>
    </ListItem>
  );
};

FolderAssetCard.propTypes = {
  folder: PropTypes.shape({
    folderName: PropTypes.string,
    modifiedTime: PropTypes.instanceOf(Date),
    folderId: PropTypes.number,
    assets: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      url: PropTypes.string,
      originalUrl: PropTypes.string,
      thumbnailUrl: PropTypes.string,
    })).isRequired,
  }).isRequired,
  i18n: PropTypes.shape({}).isRequired,
  navigateIntoFolderItem: PropTypes.func.isRequired,
  navigation: PropTypes.shape({
    gotoLibraryPage: PropTypes.func.isRequired,
  }).isRequired,
};
